import { FC, ReactNode, useEffect, useState } from 'react';

import { Link, usePage } from '@inertiajs/react';
import {
    BarChart3,
    LogOut,
    MessageSquareText,
    Package,
    PanelLeftClose,
    PanelLeftOpen,
    Plus,
    Settings,
    ShoppingBag,
    ShoppingCart,
    CircleDollarSign,
    User,
    Heart,
} from 'lucide-react';

import { Routes } from '../../models/routes';
import { LocalStorageService } from '../../services/LocalStorageService';
import { CurrentUser } from '../../types';
import { LocalStorageKeys } from '../../types/localStorage';
import { cn, formatBalanceCents } from '../../utils';
import { Button } from '../ui/Button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/Tooltip';

interface NavItem {
    label: string;
    href: string;
    icon: FC<{ className?: string }>;
    match: string;
    badge?: number;
    detail?: string;
}

interface AccountNavCounts {
    offersCount?: number;
    salesCount?: number;
    purchasesCount?: number;
    reviewsCount?: number;
    wishlistCount?: number;
    balanceCents?: number;
}

const getNavItems = (
    currentUser: CurrentUser,
    accountNav?: AccountNavCounts,
    newNotificationPresent?: boolean,
): NavItem[] => [
    {
        label: 'Профіль',
        href: `${Routes.User}/${currentUser?.nickname}`,
        icon: User,
        match: `/user/${currentUser?.nickname}`,
    },
    {
        label: 'Баланс',
        href: Routes.Balance,
        icon: CircleDollarSign,
        match: '/profile/balance',
        detail: formatBalanceCents(accountNav?.balanceCents ?? 0),
    },
    {
        label: 'Пропозиції',
        href: Routes.Offers,
        icon: Package,
        match: '/profile/offers',
        badge: accountNav?.offersCount,
    },
    {
        label: 'Аналіз цін',
        href: Routes.OffersAnalytics,
        icon: BarChart3,
        match: Routes.OffersAnalytics,
    },
    {
        label: 'Продажі',
        href: Routes.Sales,
        icon: ShoppingBag,
        match: '/profile/sales',
        badge: newNotificationPresent ? undefined : accountNav?.salesCount,
    },
    {
        label: 'Покупки',
        href: Routes.Purchases,
        icon: ShoppingCart,
        match: '/profile/purchases',
        badge: accountNav?.purchasesCount,
    },
    {
        label: 'Список бажаного',
        href: Routes.Wishlist,
        icon: Heart,
        match: Routes.Wishlist,
        badge: accountNav?.wishlistCount,
    },
    {
        label: 'Відгуки',
        href: Routes.Reviews,
        icon: MessageSquareText,
        match: '/profile/reviews',
        badge: accountNav?.reviewsCount,
    },
    {
        label: 'Редагувати профіль',
        href: Routes.ProfileEdit,
        icon: Settings,
        match: '/profile/edit',
    },
];

const NavItemLink: FC<{
    item: NavItem;
    isActive: boolean;
    isCollapsed: boolean;
    newNotificationPresent?: boolean;
    onClick?: () => void;
}> = ({ item, isActive, isCollapsed, newNotificationPresent, onClick }) => {
    const Icon = item.icon;
    const showDot =
        item.match === '/profile/sales' && newNotificationPresent && !isActive;

    const link = (
        <Link
            href={item.href}
            onClick={onClick}
            className={cn(
                'group relative flex items-center rounded-xl text-sm font-medium transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                isCollapsed ? 'h-10 justify-center px-0' : 'gap-3 px-3 py-2.5',
                isActive && 'bg-primary/10 text-primary hover:bg-primary/15',
            )}
            aria-label={isCollapsed ? item.label : undefined}
            aria-current={isActive ? 'page' : undefined}
        >
            <div className="relative">
                <Icon
                    className={cn(
                        'h-4 w-4 shrink-0',
                        isActive
                            ? 'text-primary'
                            : 'text-muted-foreground group-hover:text-accent-foreground',
                    )}
                />
                {showDot && (
                    <span className="absolute -top-1 -right-1 h-2.5 w-2.5 animate-pulse rounded-full bg-yellow-400" />
                )}
            </div>
            <span className={cn('flex-1', isCollapsed && 'sr-only')}>
                {item.label}
            </span>
            {item.detail && !isCollapsed && (
                <span className="text-xs text-muted-foreground">
                    {item.detail}
                </span>
            )}
            {item.badge != null && item.badge > 0 && (
                <span
                    className={cn(
                        'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold',
                        isCollapsed && 'absolute -top-1 -right-1',
                        isActive
                            ? 'bg-primary/20 text-primary'
                            : 'bg-muted text-muted-foreground',
                    )}
                >
                    {item.badge}
                </span>
            )}
        </Link>
    );

    if (!isCollapsed) return link;

    return (
        <Tooltip>
            <TooltipTrigger asChild>{link}</TooltipTrigger>
            <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
    );
};

const CollapsibleTooltip: FC<{
    content: string;
    children: ReactNode;
    side?: 'top' | 'right';
}> = ({ content, children, side = 'right' }) => (
    <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side}>{content}</TooltipContent>
    </Tooltip>
);

const SidebarContent: FC<{
    navItems: NavItem[];
    currentUrl: string;
    isCollapsed: boolean;
    newNotificationPresent?: boolean;
    onToggleCollapsed: () => void;
    onNavClick?: () => void;
}> = ({
    navItems,
    currentUrl,
    isCollapsed,
    newNotificationPresent,
    onToggleCollapsed,
    onNavClick,
}) => {
    const activeMatch = navItems.reduce<string | null>((longestMatch, item) => {
        if (!currentUrl.startsWith(item.match)) return longestMatch;
        if (!longestMatch || item.match.length > longestMatch.length) {
            return item.match;
        }

        return longestMatch;
    }, null);
    const toggleLabel = isCollapsed ? 'Розгорнути меню' : 'Згорнути меню';
    const ToggleIcon = isCollapsed ? PanelLeftOpen : PanelLeftClose;
    const createOfferButton = (
        <Button
            asChild
            className={cn(
                isCollapsed ? 'h-9 w-9 px-0' : 'min-w-0 flex-1 gap-2',
            )}
            size="sm"
        >
            <Link
                href={Routes.NewOffer}
                onClick={onNavClick}
                data-testid="account-sidebar-create-offer"
                aria-label={isCollapsed ? 'Створити пропозицію' : undefined}
            >
                <Plus className="h-4 w-4 shrink-0" />
                <span className={cn(isCollapsed && 'sr-only')}>Створити</span>
            </Link>
        </Button>
    );
    const toggleButton = (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={onToggleCollapsed}
            aria-label={toggleLabel}
            aria-pressed={isCollapsed}
            data-testid="account-sidebar-toggle"
        >
            <ToggleIcon className="h-4 w-4" />
        </Button>
    );
    const logoutLink = (
        <Link
            href={Routes.SignOut}
            method="delete"
            as="button"
            className={cn(
                'flex w-full items-center rounded-lg text-sm font-medium transition-colors',
                'text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                isCollapsed ? 'h-10 justify-center px-0' : 'gap-3 px-3 py-2.5',
            )}
            aria-label={isCollapsed ? 'Вийти' : undefined}
            onClick={onNavClick}
        >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className={cn(isCollapsed && 'sr-only')}>Вийти</span>
        </Link>
    );

    return (
        <div className="flex h-full flex-col">
            <div
                data-testid="account-sidebar-actions"
                className={cn(
                    'mb-2 flex gap-1.5 px-1',
                    isCollapsed ? 'flex-col items-center' : 'items-center',
                )}
            >
                {isCollapsed ? (
                    <>
                        <CollapsibleTooltip content={toggleLabel} side="right">
                            {toggleButton}
                        </CollapsibleTooltip>
                        <CollapsibleTooltip content="Створити пропозицію">
                            {createOfferButton}
                        </CollapsibleTooltip>
                    </>
                ) : (
                    <>
                        {createOfferButton}
                        <CollapsibleTooltip content={toggleLabel} side="right">
                            {toggleButton}
                        </CollapsibleTooltip>
                    </>
                )}
            </div>

            <nav className="flex-1 space-y-1 px-1" aria-label="Меню кабінету">
                {navItems.map((item) => (
                    <NavItemLink
                        key={item.href}
                        item={item}
                        isActive={item.match === activeMatch}
                        isCollapsed={isCollapsed}
                        newNotificationPresent={newNotificationPresent}
                        onClick={onNavClick}
                    />
                ))}
            </nav>

            <div
                className={cn(
                    'mt-auto border-t px-1 pt-3',
                    isCollapsed ? '-mr-3' : '-mr-6',
                )}
            >
                {isCollapsed ? (
                    <CollapsibleTooltip content="Вийти">
                        {logoutLink}
                    </CollapsibleTooltip>
                ) : (
                    logoutLink
                )}
            </div>
        </div>
    );
};

type PageProps = {
    accountNav?: AccountNavCounts;
    newNotificationPresent: boolean;
    currentUser: CurrentUser;
};

export const AccountSidebar: FC = () => {
    const { url } = usePage();
    const { accountNav, newNotificationPresent, currentUser } =
        usePage<PageProps>().props;
    const [isCollapsed, setIsCollapsed] = useState(false);

    useEffect(() => {
        setIsCollapsed(
            LocalStorageService.getItem(
                LocalStorageKeys.PROFILE_SIDEBAR_COLLAPSED,
            ) ?? false,
        );
    }, []);

    const navItems = getNavItems(
        currentUser,
        accountNav,
        newNotificationPresent,
    );
    const toggleCollapsed = () => {
        setIsCollapsed((currentValue) => {
            const nextValue = !currentValue;

            LocalStorageService.setItem(
                LocalStorageKeys.PROFILE_SIDEBAR_COLLAPSED,
                nextValue,
            );

            return nextValue;
        });
    };

    return (
        <aside
            data-testid="account-sidebar"
            className={cn(
                "sticky top-12 isolate hidden h-[calc(100vh-3rem)] shrink-0 border-r border-border/80 py-6 transition-[width,padding] duration-200 before:absolute before:inset-y-0 before:right-0 before:-z-10 before:w-screen before:bg-muted/20 before:content-[''] lg:block",
                isCollapsed ? 'w-16 pr-3' : 'w-58 pr-6',
            )}
            aria-label="Навігація кабінету"
        >
            <TooltipProvider delayDuration={200}>
                <SidebarContent
                    navItems={navItems}
                    currentUrl={url}
                    isCollapsed={isCollapsed}
                    newNotificationPresent={newNotificationPresent}
                    onToggleCollapsed={toggleCollapsed}
                />
            </TooltipProvider>
        </aside>
    );
};
